import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { CircularProgress, Dialog, DialogContent } from '@mui/material'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import { ConfirmDialog, FplDialogTitle } from 'components/common'
import { DEFAULT_COUNTRY_ID, VatCategoryType } from 'constants/index'
import {
  DriverLicenseType,
  DriverOnboardingDtoInput,
  PaymentTerm,
  useGetDriverByCurrentUserQuery,
  useUpdateDriverOnboardingMutation,
} from 'generated/graphql'

import DriverOnboardingDialogValidation from './DriverOnboardingDialog.validation'
import OnboardingStepperForm from './OnboardingStepperForm'

export default function DriverOnboardingDialog() {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false)
  const [selectedVatCategoryId, setSelectedVatCategoryId] = useState(String(VatCategoryType.t0))

  const handleDialog = () => {
    setIsOpen((prev) => !prev)
  }

  const handleClose = () => {
    setOpenConfirmDialog(true)
  }

  const handleConfirmDialog = () => {
    setIsOpen(false)
  }

  const { data: driverByCurrentUserData, loading: driverByCurrentUserLoading } =
    useGetDriverByCurrentUserQuery()

  const methods = useForm<DriverOnboardingDtoInput>({
    resolver: yupResolver(DriverOnboardingDialogValidation(selectedVatCategoryId)),
    mode: 'onChange',
    defaultValues: {
      name: '',
      postcode: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryId: String(DEFAULT_COUNTRY_ID),
      email: '',
      telephoneNumber: '',
      startDate: new Date(),
      paymentTerm: PaymentTerm.MonthFromEndOfInvoiceMoth,
      invoiceEmail: '',
      eoriNumber: '',
      currencyId: '',
      licenseType: DriverLicenseType.Standard,
      licenseExpiryDate: null,
      endorsements: '',
      vatCategoryId: '',
      bankName: '',
      bankAccountNumber: '',
      vatIdNumber: '',
      bankSortCode: '',
      bankAccountHolderName: '',
      alternativeTelephoneNumber: '',
      internationalBankAccountNumber: '',
      companyRegistrationNumber: '',
    },
  })
  const {
    control,
    clearErrors,
    formState: { errors },
    reset,
    handleSubmit,
  } = methods

  const hasFormErrors = Object.keys(errors).length !== 0

  const watchedVatCategoryId = useWatch({
    control,
    name: 'vatCategoryId',
  })

  useEffect(() => {
    if (watchedVatCategoryId && watchedVatCategoryId !== selectedVatCategoryId) {
      setSelectedVatCategoryId(watchedVatCategoryId)
    }
    clearErrors('vatIdNumber')
  }, [watchedVatCategoryId, selectedVatCategoryId, clearErrors])

  useEffect(() => {
    if (
      driverByCurrentUserData &&
      !driverByCurrentUserData?.driverByCurrentUser?.isOnboardingCompleted
    ) {
      handleDialog()
    }
    if (driverByCurrentUserData?.driverByCurrentUser && !driverByCurrentUserLoading) {
      const driver = driverByCurrentUserData.driverByCurrentUser
      reset({
        name: driver.name,
        addressLine1: driver.addressLine1,
        addressLine2: driver.addressLine2,
        city: driver.city,
        countryId: String(driver.countryId),
        postcode: driver.postcode,
        email: driver.email,
        invoiceEmail: driver.invoiceEmail,
        telephoneNumber: driver.telephoneNumber,
        alternativeTelephoneNumber: driver.alternativeTelephoneNumber,
        startDate: driver.startDate,
        paymentTerm: driver.paymentTerm,
        eoriNumber: driver.eoriNumber,
        licenseType: driver.licenseType,
        licenseExpiryDate: driver.licenseExpiryDate,
        endorsements: driver.endorsements,
        vatCategoryId: String(driver.vatCategoryId ?? ''),
        vatIdNumber: driver.vatIdNumber,
        bankName: driver.bankName,
        bankAccountNumber: driver.bankAccountNumber ?? '',
        bankAccountHolderName: driver.bankAccountHolderName ?? '',
        bankSortCode: driver.bankSortCode ?? '',
        internationalBankAccountNumber: driver.internationalBankAccountNumber,
        swift: driver.swift,
        companyRegistrationNumber: driver.companyRegistrationNumber,
        currencyId: String(driver.currencyId ?? ''),
      })
    }
  }, [driverByCurrentUserData, driverByCurrentUserLoading, reset])

  const [updateDriverOnboarding, { loading: updatingDriver }] = useUpdateDriverOnboardingMutation()

  const onSubmit: SubmitHandler<DriverOnboardingDtoInput> = async (data) => {
    try {
      const result = await updateDriverOnboarding({
        variables: {
          input: data,
          isMobileOnboarding: false,
        },
      })

      if (!result.data?.updateDriverOnboarding?.id) {
        toast.error(`The Onboarding process could not be completed.`)
        return
      }

      toast.success(`The Onboarding process was successfully completed.`)
      handleDialog()
    } catch {
      toast.error(`The Onboarding process could not be completed.`)
    }
  }

  return (
    <>
      {driverByCurrentUserLoading ? (
        <CircularProgress />
      ) : (
        <Dialog fullScreen={true} open={isOpen} aria-label='driver-onboarding-dialog' keepMounted>
          <FplDialogTitle id='driver-onboarding-dialog' onClose={handleClose}>
            Onboarding
          </FplDialogTitle>
          <DialogContent>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <OnboardingStepperForm disabled={hasFormErrors} loading={updatingDriver} />
              </form>
            </FormProvider>
          </DialogContent>
        </Dialog>
      )}
      {openConfirmDialog && (
        <ConfirmDialog
          message={
            'Are you sure you want to end the on-boarding? Doing so will take you to your profile, but you will be required to finish the on-boarding next time.'
          }
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={handleConfirmDialog}
        />
      )}
    </>
  )
}
